import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Cake, FileSignature, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";
import { PageSkeleton } from "../PageSkeleton";
import { ProfileToggles } from "./ProfileToggles";
import { DocumentRow } from "./DocumentRow";
import { RachunkiGrid } from "./RachunkiGrid";
import { EMPLOYMENT, fmtDate, initials, healthTone } from "./hrUtils";

export const EmployeeFile = ({ employeeId, highlightDoc, onBack, onChanged }) => {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => api.get(`/hr/employees/${employeeId}`).then((r) => { setFile(r.data); setError(null); }).catch((e) => setError(e?.response?.data?.detail || "Nie udało się wczytać teczki")), [employeeId]);
  useEffect(() => { setFile(null); load(); }, [load]);
  const refresh = () => { load(); onChanged?.(); };

  if (error) return <div className="p-6 text-sm text-red-700" data-testid="hr-file-error">{error} <button type="button" onClick={onBack} className="underline ml-2">Wróć</button></div>;
  if (!file) return <PageSkeleton variant="list" label="Ładowanie teczki…" />;

  const e = file.employee;
  const emp = EMPLOYMENT[file.employment_type] || EMPLOYMENT.none;
  const groups = [...new Set(file.items.map((i) => i.group))];
  const urgent = file.todos.filter((t) => t.urgency <= 1);

  return (
    <div data-testid="hr-employee-file" className="page-enter space-y-6">
      <div className="flex flex-wrap items-center gap-4">
        <button type="button" onClick={onBack} data-testid="hr-file-back" className="h-9 px-3 inline-flex items-center gap-1.5 ring-1 ring-zinc-300 hover:ring-zinc-900 text-xs font-bold"><ArrowLeft className="w-4 h-4" />Wszyscy</button>
        <span className="w-12 h-12 rounded-full bg-zinc-900 text-white flex items-center justify-center text-sm font-black">{initials(e.first_name, e.last_name)}</span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading font-black text-xl md:text-2xl uppercase tracking-tight truncate" data-testid="hr-file-name">{e.first_name} {e.last_name}</h2>
          <div className="text-[11px] font-mono text-zinc-500 flex flex-wrap gap-x-3">
            <span>{e.position || "—"}</span>
            <span data-testid="hr-file-employment" className="font-bold text-zinc-800">{emp.label}</span>
            {file.current_contract && <span className="inline-flex items-center gap-1"><FileSignature className="w-3 h-3" />{file.current_contract.contract_number} · {fmtDate(file.current_contract.start_date)} – {file.current_contract.end_date ? fmtDate(file.current_contract.end_date) : "bezterminowo"}</span>}
            {e.birth_date && <span className="inline-flex items-center gap-1"><Cake className="w-3 h-3" />{fmtDate(e.birth_date)}{file.age26_date ? ` · 26 lat: ${fmtDate(file.age26_date)}` : ""}</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">Kompletność</div>
          <div className="font-heading font-black text-2xl" data-testid="hr-file-health">{file.health == null ? "—" : `${file.health}%`}</div>
          <div className="h-1.5 w-28 bg-zinc-100 rounded-full overflow-hidden"><div className={`h-full ${healthTone(file.health ?? 0)}`} style={{ width: `${file.health ?? 0}%` }} /></div>
        </div>
      </div>

      {urgent.length > 0 && (
        <div data-testid="hr-file-alerts" className="border border-red-200 bg-red-50 p-3 space-y-1">
          {urgent.map((t, i) => <div key={i} className="text-sm text-red-800 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{t.label}</div>)}
        </div>
      )}

      <ProfileToggles key={JSON.stringify(file.profile)} file={file} onSaved={refresh} />

      <section className="space-y-4" data-testid="hr-documents">
        <div>
          <div className="font-label">// Teczka osobowa</div>
          <h3 className="font-heading font-black uppercase tracking-tight">Dokumenty</h3>
        </div>
        {groups.map((g) => (
          <div key={g} className="space-y-2" data-testid={`hr-doc-group-${g.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <div className="text-[10px] font-black uppercase tracking-widest text-zinc-500">{g}</div>
            {file.items.filter((i) => i.group === g).map((it) => (
              <DocumentRow key={it.doc_type} item={it} employeeId={e.id} onSaved={refresh} highlight={highlightDoc === it.doc_type} />
            ))}
          </div>
        ))}
      </section>

      <RachunkiGrid file={file} onSaved={refresh} />
    </div>
  );
};
